<script>
import api from "@/helpers/api-rest/api.js";
import { required, maxLength, helpers } from "vuelidate/lib/validators";
import { errorMessages } from "@/helpers/validations/errorMessages";
import { notifyValidationFailedAndFocusFirstInvalid } from "@/helpers/validations/vuelidateFormFeedback";
import BasicInfoTab from "./partials/BasicInfoTab.vue";
import StrategicLinesTab from "./partials/StrategicLinesTab.vue";
import NameRelationTab from "./partials/NameRelationTab.vue";

// Custom validator for strategic lines description (max 1000 chars)
const maxLength1000 = maxLength(1000);

// Custom validator for name fields (max 255 chars)
const maxLength255 = maxLength(255);

export default {
  name: "ProjectModal",
  components: {
    BasicInfoTab,
    StrategicLinesTab,
    NameRelationTab,
  },
  props: {
    project: {
      type: Object,
      default: () => {
        return {
          name: "",
          description: "",
          link: "",
          project_type_id: "",
          status: "",
        };
      },
    },
    duplicate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectOptionsProjectType: [],
      selectOptionsStatus: [
        { id: 1, name: "Activo" },
        { id: 0, name: "Inactivo" },
      ],
      loading: false,
      loadingProject: false,
      localProject: {},
      activeTab: 0,
      // Relations data
      strategicLines: [],
      subsidizableActuations: [],
      subsidizableActions: [],
      codings: [],
      phases: [],
      primaryProjects: [],
      expenseTypologies: [],
      errorMessages,
    };
  },
  watch: {
    project: {
      immediate: true,
      deep: true,
      handler(newProject) {
        if (newProject && newProject.id) {
          if (this.duplicate) {
            this.loadFullProjectForDuplicate(newProject.id);
          } else {
            this.loadFullProject(newProject.id);
          }
        } else if (newProject) {
          this.initializeProject(newProject);
        } else {
          this.initializeProject({});
        }
      },
    },
    duplicate: {
      immediate: true,
      handler(isDuplicate) {
        if (isDuplicate && this.project && this.project.id) {
          this.loadFullProjectForDuplicate(this.project.id);
        }
      },
    },
  },
  computed: {
    modalTitle() {
      if (this.duplicate) {
        return "Duplicar Convocatoria";
      }
      return this.localProject?.id ? "Editar Convocatoria" : "Añadir Convocatoria";
    },
    submitButtonText() {
      return this.localProject?.id ? "Guardar" : "Añadir Convocatoria";
    },
    strategicLinesCount() {
      return this.strategicLines.length;
    },
    subsidizableActuationsCount() {
      return this.subsidizableActuations.length;
    },
    subsidizableActionsCount() {
      return this.subsidizableActions.length;
    },
    codingsCount() {
      return this.codings.length;
    },
    phasesCount() {
      return this.phases.length;
    },
    primaryProjectsCount() {
      return this.primaryProjects.length;
    },
    expenseTypologiesCount() {
      return this.expenseTypologies.length;
    },
    // Computed properties for field errors - extract from $v for reactivity
    strategicLinesErrors() {
      const errors = {};
      if (this.$v.strategicLines?.$each) {
        this.strategicLines.forEach((line, index) => {
          const validation = this.$v.strategicLines.$each[index] || this.$v.strategicLines.$each[String(index)];
          if (validation?.description) {
            errors[index] = {
              hasError: validation.description.$dirty && validation.description.$error,
              required: validation.description.required,
              maxLength: validation.description.maxLength,
            };
          }
        });
      }
      return errors;
    },
    subsidizableActuationsErrors() {
      return this.getRelationErrors('subsidizableActuations');
    },
    subsidizableActionsErrors() {
      return this.getRelationErrors('subsidizableActions');
    },
    codingsErrors() {
      return this.getRelationErrors('codings');
    },
    phasesErrors() {
      return this.getRelationErrors('phases');
    },
    primaryProjectsErrors() {
      return this.getRelationErrors('primaryProjects');
    },
    expenseTypologiesErrors() {
      return this.getRelationErrors('expenseTypologies');
    },
  },
  created() {
    this.loadSelectOptionsProjectType();
  },
  methods: {
    initializeProject(project) {
      this.localProject = { 
        ...project,
        status: project.status ?? 1
      };
      this.resetAllRelations();
    },
    resetAllRelations() {
      this.strategicLines = [];
      this.subsidizableActuations = [];
      this.subsidizableActions = [];
      this.codings = [];
      this.phases = [];
      this.primaryProjects = [];
      this.expenseTypologies = [];
    },
    loadFullProject(projectId) {
      this.loadingProject = true;
      const apiEndpoint = `/api/v1/projects/${projectId}`;
      api
        .get({ url: apiEndpoint })
        .then((response) => {
          const fullProject = response?.data?.data;
          this.localProject = { 
            ...fullProject,
            status: fullProject.status ?? 1
          };
          // Initialize all relations from project data
          this.strategicLines = fullProject.strategic_lines
            ? fullProject.strategic_lines.map((line) => ({
                id: line.id,
                description: line.description,
              }))
            : [];
          this.subsidizableActuations = fullProject.subsidizable_actuations
            ? fullProject.subsidizable_actuations.map((item) => ({
                id: item.id,
                name: item.name,
              }))
            : [];
          this.subsidizableActions = fullProject.subsidizable_actions
            ? fullProject.subsidizable_actions.map((item) => ({
                id: item.id,
                name: item.name,
              }))
            : [];
          this.codings = fullProject.codings
            ? fullProject.codings.map((item) => ({
                id: item.id,
                name: item.name,
              }))
            : [];
          this.phases = fullProject.phases
            ? fullProject.phases.map((item) => ({
                id: item.id,
                name: item.name,
              }))
            : [];
          this.primaryProjects = fullProject.primary_projects
            ? fullProject.primary_projects.map((item) => ({
                id: item.id,
                name: item.name,
              }))
            : [];
          this.expenseTypologies = fullProject.expense_typologies
            ? fullProject.expense_typologies.map((item) => ({
                id: item.id,
                name: item.name,
              }))
            : [];
          this.loadingProject = false;
        })
        .catch((error) => {
          console.error("Error loading project:", error);
          this.loadingProject = false;
          this.initializeProject(this.project);
        });
    },
    loadFullProjectForDuplicate(projectId) {
      this.loadingProject = true;
      const apiEndpoint = `/api/v1/projects/${projectId}`;
      api
        .get({ url: apiEndpoint })
        .then((response) => {
          const fullProject = response?.data?.data;
          // Clone project data and reset ID
          const clonedProject = { ...fullProject };
          delete clonedProject.id;
          // Add "- copia" suffix to name
          clonedProject.name = clonedProject.name ? `${clonedProject.name} - copia` : " - copia";
          // Ensure status has default value
          clonedProject.status = clonedProject.status ?? 1;
          this.localProject = clonedProject;
          // Clone all relations and reset their IDs
          this.strategicLines = fullProject.strategic_lines
            ? fullProject.strategic_lines.map((line) => ({
                id: null,
                description: line.description,
              }))
            : [];
          this.subsidizableActuations = fullProject.subsidizable_actuations
            ? fullProject.subsidizable_actuations.map((item) => ({
                id: null,
                name: item.name,
              }))
            : [];
          this.subsidizableActions = fullProject.subsidizable_actions
            ? fullProject.subsidizable_actions.map((item) => ({
                id: null,
                name: item.name,
              }))
            : [];
          this.codings = fullProject.codings
            ? fullProject.codings.map((item) => ({
                id: null,
                name: item.name,
              }))
            : [];
          this.phases = fullProject.phases
            ? fullProject.phases.map((item) => ({
                id: null,
                name: item.name,
              }))
            : [];
          this.primaryProjects = fullProject.primary_projects
            ? fullProject.primary_projects.map((item) => ({
                id: null,
                name: item.name,
              }))
            : [];
          this.expenseTypologies = fullProject.expense_typologies
            ? fullProject.expense_typologies.map((item) => ({
                id: null,
                name: item.name,
              }))
            : [];
          this.loadingProject = false;
        })
        .catch((error) => {
          console.error("Error loading project for duplicate:", error);
          this.loadingProject = false;
          this.$bvToast.toast("Error al cargar el proyecto para duplicar", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        });
    },
    loadSelectOptionsProjectType() {
      const apiEndpoint = `/api/v1/project-types`;
      api
        .get({ url: apiEndpoint })
        .then((response) => {
          this.selectOptionsProjectType = response?.data?.data;
        })
        .catch((error) => {
          console.error("Error al cargar los tipos de convocatorias:", error);
        });
    },
    // Strategic lines methods
    addStrategicLine() {
      this.strategicLines.push({
        id: null,
        description: "",
      });
    },
    removeStrategicLine(index) {
      if (confirm("¿Está seguro de que desea eliminar este elemento?")) {
        this.strategicLines.splice(index, 1);
      }
    },
    updateStrategicLine(index, value) {
      this.$set(this.strategicLines[index], "description", value);
    },
    touchStrategicLine(index) {
      const validation = this.$v.strategicLines?.$each?.[index] || this.$v.strategicLines?.$each?.[String(index)];
      if (validation?.description?.$touch) {
        validation.description.$touch();
      }
    },
    // Generic methods for name-based relations
    addRelationItem(relationType) {
      this[relationType].push({
        id: null,
        name: "",
      });
    },
    removeRelationItem(relationType, index) {
      if (confirm("¿Está seguro de que desea eliminar este elemento?")) {
        this[relationType].splice(index, 1);
      }
    },
    updateRelationItem(relationType, index, value) {
      this.$set(this[relationType][index], "name", value);
    },
    touchRelationField(relationType, index) {
      const validationObj = this.$v[relationType];
      if (validationObj?.$each) {
        const validation = validationObj.$each[index] || validationObj.$each[String(index)];
        if (validation?.name?.$touch) {
          validation.name.$touch();
        }
      }
    },
    // Specific methods for each relation type
    addSubsidizableActuation() {
      this.addRelationItem("subsidizableActuations");
    },
    removeSubsidizableActuation(index) {
      this.removeRelationItem("subsidizableActuations", index);
    },
    updateSubsidizableActuation(index, value) {
      this.updateRelationItem("subsidizableActuations", index, value);
    },
    addSubsidizableAction() {
      this.addRelationItem("subsidizableActions");
    },
    removeSubsidizableAction(index) {
      this.removeRelationItem("subsidizableActions", index);
    },
    updateSubsidizableAction(index, value) {
      this.updateRelationItem("subsidizableActions", index, value);
    },
    addCoding() {
      this.addRelationItem("codings");
    },
    removeCoding(index) {
      this.removeRelationItem("codings", index);
    },
    updateCoding(index, value) {
      this.updateRelationItem("codings", index, value);
    },
    addPhase() {
      this.addRelationItem("phases");
    },
    removePhase(index) {
      this.removeRelationItem("phases", index);
    },
    updatePhase(index, value) {
      this.updateRelationItem("phases", index, value);
    },
    addPrimaryProject() {
      this.addRelationItem("primaryProjects");
    },
    removePrimaryProject(index) {
      this.removeRelationItem("primaryProjects", index);
    },
    updatePrimaryProject(index, value) {
      this.updateRelationItem("primaryProjects", index, value);
    },
    addExpenseTypology() {
      this.addRelationItem("expenseTypologies");
    },
    removeExpenseTypology(index) {
      this.removeRelationItem("expenseTypologies", index);
    },
    updateExpenseTypology(index, value) {
      this.updateRelationItem("expenseTypologies", index, value);
    },
    // Helper method to get relation errors
    getRelationErrors(relationType) {
      const errors = {};
      const validationObj = this.$v[relationType];
      const items = this[relationType];
      if (validationObj?.$each && items) {
        items.forEach((item, index) => {
          const validation = validationObj.$each[index] || validationObj.$each[String(index)];
          if (validation?.name) {
            errors[index] = {
              hasError: validation.name.$dirty && validation.name.$error,
              required: validation.name.required,
              maxLength: validation.name.maxLength,
            };
          }
        });
      }
      return errors;
    },
    prepareStrategicLines() {
      return this.strategicLines
        .filter((line) => line.description && line.description.trim() !== "")
        .map((line) => ({
          id: line.id || null,
          description: line.description.trim(),
        }));
    },
    prepareNameRelation(relation) {
      return relation
        .filter((item) => item.name && item.name.trim() !== "")
        .map((item) => ({
          id: item.id || null,
          name: item.name.trim(),
        }));
    },
    assignRelationsToProjectData(projectData, preparedRelations, isUpdate) {
      const relationMap = {
        strategic_lines: preparedRelations.strategicLines,
        subsidizable_actuations: preparedRelations.subsidizableActuations,
        subsidizable_actions: preparedRelations.subsidizableActions,
        codings: preparedRelations.codings,
        phases: preparedRelations.phases,
        primary_projects: preparedRelations.primaryProjects,
        expense_typologies: preparedRelations.expenseTypologies,
      };

      Object.keys(relationMap).forEach((key) => {
        const relation = relationMap[key];
        if (isUpdate || relation.length > 0) {
          projectData[key] = relation;
        }
      });
    },
    prepareProjectData() {
      const projectData = {
        name: this.localProject.name,
        description: this.localProject.description,
        link: this.localProject.link,
        status: this.localProject.status ?? 1,
        project_type_id: this.localProject.project_type_id,
      };

      const preparedRelations = {
        strategicLines: this.prepareStrategicLines(),
        subsidizableActuations: this.prepareNameRelation(
          this.subsidizableActuations
        ),
        subsidizableActions: this.prepareNameRelation(
          this.subsidizableActions
        ),
        codings: this.prepareNameRelation(this.codings),
        phases: this.prepareNameRelation(this.phases),
        primaryProjects: this.prepareNameRelation(this.primaryProjects),
        expenseTypologies: this.prepareNameRelation(this.expenseTypologies),
      };

      const isUpdate = !!this.localProject?.id;
      this.assignRelationsToProjectData(projectData, preparedRelations, isUpdate);

      return projectData;
    },
    touchAllFields() {
      // Touch all array fields to show validation errors
      // Strategic lines
      this.strategicLines.forEach((item, index) => {
        this.touchStrategicLine(index);
      });
      // Name-based relations
      const nameRelations = [
        'subsidizableActuations',
        'subsidizableActions',
        'codings',
        'phases',
        'primaryProjects',
        'expenseTypologies',
      ];
      nameRelations.forEach((relationType) => {
        const items = this[relationType];
        items.forEach((item, index) => {
          this.touchRelationField(relationType, index);
        });
      });
    },
    createProject() {
      this.$v.$touch();
      this.touchAllFields();
      if (this.$v.$invalid) {
        // Switch to first tab with errors
        if (
          this.$v.localProject.name.$error ||
          this.$v.localProject.description.$error ||
          this.$v.localProject.project_type_id.$error ||
          this.$v.localProject.status.$error
        ) {
          this.activeTab = 0;
        }
        notifyValidationFailedAndFocusFirstInvalid(this, {
          rootEl: "#ModalProject",
        });
        return;
      }

      this.loading = true;
      const apiEndpoint = `/api/v1/projects`;
      const projectData = this.prepareProjectData();

      api
        .post({ url: apiEndpoint, data: projectData })
        .then((response) => {
          this.loading = false;
          const newProject = response?.data?.data;
          this.$emit("success", newProject);
          this.$bvModal.hide("ModalProject");
          this.resetForm();
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error al crear la convocatoria:", error);
          this.$bvToast.toast(
            error?.response?.data?.message || "Error al crear la convocatoria",
            {
              title: "Error",
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    updateProject() {
      this.$v.$touch();
      this.touchAllFields();
      if (this.$v.$invalid) {
        // Switch to first tab with errors
        if (
          this.$v.localProject.name.$error ||
          this.$v.localProject.description.$error ||
          this.$v.localProject.project_type_id.$error ||
          this.$v.localProject.status.$error
        ) {
          this.activeTab = 0;
        }
        notifyValidationFailedAndFocusFirstInvalid(this, {
          rootEl: "#ModalProject",
        });
        return;
      }

      this.loading = true;
      const apiEndpoint = `/api/v1/projects`;
      const projectData = this.prepareProjectData();

      api
        .put({
          url: apiEndpoint,
          data: projectData,
          id: this.localProject.id,
        })
        .then((response) => {
          this.loading = false;
          const updatedProject = response?.data?.data;
          this.$emit("success", updatedProject);
          this.$bvModal.hide("ModalProject");
          this.resetForm();
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error editing convocatoria:", error);
          this.$bvToast.toast(
            error?.response?.data?.message ||
              "Error al actualizar la convocatoria",
            {
              title: "Error",
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    submitForm() {
      if (this.localProject?.id) {
        return this.updateProject();
      }
      return this.createProject();
    },
    cancelForm() {
      this.$v.$reset();
      this.$bvModal.hide("ModalProject");
      this.$emit("close-modal");
      this.resetForm();
    },
    onModalHidden() {
      // Reset duplicate mode when modal is closed
      this.$emit("reset-duplicate");
      this.resetForm();
    },
    resetForm() {
      this.resetAllRelations();
      this.localProject = {};
      this.activeTab = 0;
      this.$v.$reset();
    },
    // Update methods for localProject
    updateName(value) {
      this.$set(this.localProject, "name", value);
    },
    updateDescription(value) {
      this.$set(this.localProject, "description", value);
    },
    updateProjectTypeId(value) {
      this.$set(this.localProject, "project_type_id", value);
    },
    updateLink(value) {
      this.$set(this.localProject, "link", value);
    },
    updateStatus(value) {
      this.$set(this.localProject, "status", value);
    },
  },
  validations: {
    localProject: {
      name: { required },
      description: { required },
      project_type_id: { required },
      status: { required },
    },
    strategicLines: {
      $each: {
        description: {
          required: helpers.withParams(
            { type: "required" },
            (value) => value && value.trim() !== ""
          ),
          maxLength: helpers.withParams(
            { type: "maxLength", max: 1000 },
            maxLength1000
          ),
        },
      },
    },
    subsidizableActuations: {
      $each: {
        name: {
          required: helpers.withParams(
            { type: "required" },
            (value) => value && value.trim() !== ""
          ),
          maxLength: helpers.withParams(
            { type: "maxLength", max: 255 },
            maxLength255
          ),
        },
      },
    },
    subsidizableActions: {
      $each: {
        name: {
          required: helpers.withParams(
            { type: "required" },
            (value) => value && value.trim() !== ""
          ),
          maxLength: helpers.withParams(
            { type: "maxLength", max: 255 },
            maxLength255
          ),
        },
      },
    },
    codings: {
      $each: {
        name: {
          required: helpers.withParams(
            { type: "required" },
            (value) => value && value.trim() !== ""
          ),
          maxLength: helpers.withParams(
            { type: "maxLength", max: 255 },
            maxLength255
          ),
        },
      },
    },
    phases: {
      $each: {
        name: {
          required: helpers.withParams(
            { type: "required" },
            (value) => value && value.trim() !== ""
          ),
          maxLength: helpers.withParams(
            { type: "maxLength", max: 255 },
            maxLength255
          ),
        },
      },
    },
    primaryProjects: {
      $each: {
        name: {
          required: helpers.withParams(
            { type: "required" },
            (value) => value && value.trim() !== ""
          ),
          maxLength: helpers.withParams(
            { type: "maxLength", max: 255 },
            maxLength255
          ),
        },
      },
    },
    expenseTypologies: {
      $each: {
        name: {
          required: helpers.withParams(
            { type: "required" },
            (value) => value && value.trim() !== ""
          ),
          maxLength: helpers.withParams(
            { type: "maxLength", max: 255 },
            maxLength255
          ),
        },
      },
    },
  },
};
</script>
<template>
  <b-modal
    id="ModalProject"
    size="extra-xl"
    :title="modalTitle"
    no-close-on-backdrop
    no-close-on-esc
    class="project-modal modal-extra-xl"
    @hidden="onModalHidden"
  >
    <template #default="{}">
      <div v-if="loadingProject" class="text-center p-4">
        <div class="spinner-border">
          <span class="sr-only">Cargando...</span>
        </div>
      </div>
      <form v-else @submit.prevent="submitForm" enctype="multipart/form-data">
        <b-tabs v-model="activeTab" content-class="mt-3" pills justified>
          <!-- Tab 1: Información General -->
          <b-tab>
            <template v-slot:title>
              <i class="fas fa-info-circle mr-1"></i>
              <span class="d-none d-md-inline">Información General</span>
              <b-badge
                v-if="
                  $v.localProject.name.$error ||
                  $v.localProject.description.$error ||
                  $v.localProject.project_type_id.$error ||
                  $v.localProject.status.$error
                "
                variant="danger"
                class="ml-1"
                >!</b-badge
              >
            </template>
            <BasicInfoTab
              :local-project="localProject"
              :select-options-project-type="selectOptionsProjectType"
              :select-options-status="selectOptionsStatus"
              :v="$v.localProject"
              @update:name="updateName"
              @update:description="updateDescription"
              @update:project_type_id="updateProjectTypeId"
              @update:status="updateStatus"
              @update:link="updateLink"
            />
          </b-tab>

          <!-- Tab 2: Líneas Estratégicas -->
          <b-tab>
            <template v-slot:title>
              <i class="fas fa-list-ul mr-1"></i>
              <span class="d-none d-md-inline">Líneas Estratégicas</span>
              <b-badge variant="secondary" class="ml-1">{{
                strategicLinesCount
              }}</b-badge>
              <b-badge
                v-if="$v.strategicLines.$error"
                variant="danger"
                class="ml-1"
                >!</b-badge
              >
            </template>
            <StrategicLinesTab
              :strategic-lines="strategicLines"
              :field-errors="strategicLinesErrors"
              @add-item="addStrategicLine"
              @remove-item="removeStrategicLine"
              @update-item="updateStrategicLine"
              @touch-field="touchStrategicLine"
            />
          </b-tab>

          <!-- Tab 3: Actuaciones subvencionables -->
          <b-tab>
            <template v-slot:title>
              <i class="fas fa-tasks mr-1"></i>
              <span class="d-none d-md-inline">{{ $t('project.relations.subsidizable_actuation') }}</span>
              <b-badge variant="secondary" class="ml-1">{{
                subsidizableActuationsCount
              }}</b-badge>
              <b-badge
                v-if="$v.subsidizableActuations.$error"
                variant="danger"
                class="ml-1"
                >!</b-badge
              >
            </template>
            <NameRelationTab
              :title="$t('project.relations.subsidizable_actuation')"
              :items="subsidizableActuations"
              :field-errors="subsidizableActuationsErrors"
              empty-message="No hay actuaciones subvencionables. Haz clic en 'Añadir' para agregar una."
              item-label="Actuación"
              placeholder="Nombre de la actuación subvencionable (máx. 255 caracteres)"
              field-id="subsidizable-actuation"
              @add-item="addSubsidizableActuation"
              @remove-item="removeSubsidizableActuation"
              @update-item="updateSubsidizableActuation"
              @touch-field="(index) => touchRelationField('subsidizableActuations', index)"
            />
          </b-tab>

          <!-- Tab 4: Acciones subvencionables -->
          <b-tab>
            <template v-slot:title>
              <i class="fas fa-hand-paper mr-1"></i>
              <span class="d-none d-md-inline">{{ $t('project.relations.subsidizable_action') }}</span>
              <b-badge variant="secondary" class="ml-1">{{
                subsidizableActionsCount
              }}</b-badge>
              <b-badge
                v-if="$v.subsidizableActions.$error"
                variant="danger"
                class="ml-1"
                >!</b-badge
              >
            </template>
            <NameRelationTab
              :title="$t('project.relations.subsidizable_action')"
              :items="subsidizableActions"
              :field-errors="subsidizableActionsErrors"
              empty-message="No hay acciones subvencionables. Haz clic en 'Añadir' para agregar una."
              item-label="Acción"
              placeholder="Nombre de la acción subvencionable (máx. 255 caracteres)"
              field-id="subsidizable-action"
              @add-item="addSubsidizableAction"
              @remove-item="removeSubsidizableAction"
              @update-item="updateSubsidizableAction"
              @touch-field="(index) => touchRelationField('subsidizableActions', index)"
            />
          </b-tab>

          <!-- Tab 5: Codificación -->
          <b-tab>
            <template v-slot:title>
              <i class="fas fa-code mr-1"></i>
              <span class="d-none d-md-inline">{{ $t('project.relations.coding') }}</span>
              <b-badge variant="secondary" class="ml-1">{{
                codingsCount
              }}</b-badge>
              <b-badge
                v-if="$v.codings.$error"
                variant="danger"
                class="ml-1"
                >!</b-badge
              >
            </template>
            <NameRelationTab
              :title="$t('project.relations.coding')"
              :items="codings"
              :field-errors="codingsErrors"
              empty-message="No hay codificaciones. Haz clic en 'Añadir' para agregar una."
              item-label="Codificación"
              placeholder="Nombre de la codificación (máx. 255 caracteres)"
              field-id="coding"
              @add-item="addCoding"
              @remove-item="removeCoding"
              @update-item="updateCoding"
              @touch-field="(index) => touchRelationField('codings', index)"
            />
          </b-tab>

          <!-- Tab 6: Fase -->
          <b-tab>
            <template v-slot:title>
              <i class="fas fa-layer-group mr-1"></i>
              <span class="d-none d-md-inline">{{ $t('project.relations.phase') }}</span>
              <b-badge variant="secondary" class="ml-1">{{
                phasesCount
              }}</b-badge>
              <b-badge
                v-if="$v.phases.$error"
                variant="danger"
                class="ml-1"
                >!</b-badge
              >
            </template>
            <NameRelationTab
              :title="$t('project.relations.phase')"
              :items="phases"
              :field-errors="phasesErrors"
              empty-message="No hay fases. Haz clic en 'Añadir' para agregar una."
              item-label="Fase"
              placeholder="Nombre de la fase (máx. 255 caracteres)"
              field-id="phase"
              @add-item="addPhase"
              @remove-item="removePhase"
              @update-item="updatePhase"
              @touch-field="(index) => touchRelationField('phases', index)"
            />
          </b-tab>

          <!-- Tab 7: Proyecto Primario -->
          <b-tab>
            <template v-slot:title>
              <i class="fas fa-project-diagram mr-1"></i>
              <span class="d-none d-md-inline">{{ $t('project.relations.primary_project') }}</span>
              <b-badge variant="secondary" class="ml-1">{{
                primaryProjectsCount
              }}</b-badge>
              <b-badge
                v-if="$v.primaryProjects.$error"
                variant="danger"
                class="ml-1"
                >!</b-badge
              >
            </template>
            <NameRelationTab
              :title="$t('project.relations.primary_project')"
              :items="primaryProjects"
              :field-errors="primaryProjectsErrors"
              empty-message="No hay proyectos primarios. Haz clic en 'Añadir' para agregar uno."
              item-label="Proyecto Primario"
              placeholder="Nombre del proyecto primario (máx. 255 caracteres)"
              field-id="primary-project"
              @add-item="addPrimaryProject"
              @remove-item="removePrimaryProject"
              @update-item="updatePrimaryProject"
              @touch-field="(index) => touchRelationField('primaryProjects', index)"
            />
          </b-tab>

          <!-- Tab 8: Tipología de gasto -->
          <b-tab>
            <template v-slot:title>
              <i class="fas fa-money-bill-wave mr-1"></i>
              <span class="d-none d-md-inline">{{ $t('project.relations.expense_typology') }}</span>
              <b-badge variant="secondary" class="ml-1">{{
                expenseTypologiesCount
              }}</b-badge>
              <b-badge
                v-if="$v.expenseTypologies.$error"
                variant="danger"
                class="ml-1"
                >!</b-badge
              >
            </template>
            <NameRelationTab
              :title="$t('project.relations.expense_typology')"
              :items="expenseTypologies"
              :field-errors="expenseTypologiesErrors"
              empty-message="No hay tipologías de gasto. Haz clic en 'Añadir' para agregar una."
              item-label="Tipología"
              placeholder="Nombre de la tipología de gasto (máx. 255 caracteres)"
              field-id="expense-typology"
              @add-item="addExpenseTypology"
              @remove-item="removeExpenseTypology"
              @update-item="updateExpenseTypology"
              @touch-field="(index) => touchRelationField('expenseTypologies', index)"
            />
          </b-tab>
        </b-tabs>
      </form>
    </template>

    <template #modal-footer="{}">
      <b-button variant="primary" @click="submitForm()" :disabled="loading">
        <span
          class="spinner-border spinner-border-sm mr-2"
          aria-hidden="false"
          v-if="loading"
        ></span>
        {{ submitButtonText }}
      </b-button>
      <b-button @click="cancelForm()">Cancelar</b-button>
    </template>
  </b-modal>
</template>
